import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Trash2, Pencil, Check, X, DollarSign, Download } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from './ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const fmt = (n) => `$${Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const fmtDate = (iso) => {
    if (!iso) return '—';
    try {
        return new Date(iso).toLocaleString('en-US', {
            timeZone: 'America/Chicago',
            month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
        });
    } catch { return iso; }
};

/**
 * Mark-paid dialog with partial-payment support + editable installment history.
 *
 * Props:
 *   row: pending payout row (must include: id, user_name, original_amount,
 *        paid_total, remaining_to_pay, paid_installments[])
 *   onClose(): fire when the dialog dismisses
 *   onChanged(): fire after every server mutation (parent should refetch)
 */
const PartialPaidDialog = ({ row, onClose, onChanged }) => {
    const [amount, setAmount] = useState('');
    const [note, setNote] = useState('');
    const [busy, setBusy] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editAmount, setEditAmount] = useState('');

    // Reset the form when a DIFFERENT row is opened and pre-fill amount = remaining.
    // Depend on row.id (not the row object) so the parent's 3s poll — which
    // hands us a fresh object with the same id every tick — can't wipe what the
    // admin is typing. The submit handler sets the remaining explicitly after a
    // partial payment, so we don't need to react to remaining changing here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (row) {
            setAmount(String(row.remaining_to_pay ?? row.original_amount ?? Math.abs(row.amount || 0)));
            setNote('');
            setEditingId(null);
        }
    }, [row?.id]);

    const submitPayment = useCallback(async () => {
        if (!row) return;
        const amt = Number(amount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error('Enter a positive amount');
            return;
        }
        setBusy(true);
        try {
            // Ambassador PAY DAY rows use a different admin endpoint that
            // doesn't support partial installments — the full amount is
            // paid in one shot. We still show the green NET banner so the
            // cashier can't accidentally send the gross.
            if (row.is_ambassador) {
                await axios.post(`${API}/admin/ambassador/pay-day/action`, {
                    transaction_id: row.id,
                    action: 'paid',
                }, { withCredentials: true });
                toast.success(`Ambassador PAY DAY marked paid — ${row.user_name}`);
                onChanged?.();
                onClose?.();
                return;
            }
            const { data } = await axios.post(`${API}/admin/cashout-mark-paid`, {
                transaction_id: row.id,
                amount: amt,
                note: note.trim() || null,
            }, { withCredentials: true });
            toast.success(data.fully_paid
                ? `Cashout fully paid — ${row.user_name}`
                : `Partial ${fmt(amt)} recorded · ${fmt(data.remaining)} still owed`);
            onChanged?.();
            if (data.fully_paid) onClose?.();
            else setAmount(String(data.remaining));
            setNote('');
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to record payment');
        } finally {
            setBusy(false);
        }
    }, [row, amount, note, onChanged, onClose]);

    const startEdit = useCallback((inst) => {
        setEditingId(inst.id);
        setEditAmount(String(inst.amount));
    }, []);

    const saveEdit = useCallback(async (inst) => {
        const amt = Number(editAmount);
        if (!Number.isFinite(amt) || amt <= 0) {
            toast.error('Enter a positive amount');
            return;
        }
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/cashout-edit-installment`, {
                transaction_id: row.id,
                installment_id: inst.id,
                amount: amt,
            }, { withCredentials: true });
            toast.success(`Installment ${fmt(data.old_amount)} → ${fmt(data.new_amount)}${data.fully_paid ? ' · cashout fully paid' : ''}`);
            setEditingId(null);
            onChanged?.();
            if (data.fully_paid) onClose?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to edit installment');
        } finally {
            setBusy(false);
        }
    }, [row, editAmount, onChanged, onClose]);

    const deleteInstallment = useCallback(async (inst) => {
        if (!window.confirm(`Delete this ${fmt(inst.amount)} installment? The cashout will owe that back.`)) return;
        setBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/cashout-delete-installment`, {
                transaction_id: row.id,
                installment_id: inst.id,
            }, { withCredentials: true });
            toast.success(`Deleted ${fmt(data.deleted_amount)}${data.fully_paid ? '' : ` · ${fmt(data.remaining)} still owed`}`);
            onChanged?.();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Failed to delete installment');
        } finally {
            setBusy(false);
        }
    }, [row, onChanged]);

    if (!row) return null;

    const original = row.original_amount ?? Math.abs(row.amount || 0);
    const owedNet = row.owed_net ?? (row.net_amount != null ? Number(row.net_amount) : original);
    const paidTotal = row.paid_total ?? 0;
    const remaining = row.remaining_to_pay ?? (owedNet - paidTotal);
    const installments = row.paid_installments || [];
    const overRemaining = Number(amount) > remaining + 0.005;

    return (
        <Dialog open={!!row} onOpenChange={(o) => { if (!o) onClose?.(); }}>
            <DialogContent
                className="bg-[#121212] border-white/10 max-w-lg text-zinc-100"
                data-testid="partial-paid-dialog"
            >
                <DialogHeader>
                    <DialogTitle className="text-white flex items-center gap-2">
                        <DollarSign className="w-4 h-4 text-emerald-400" />
                        Mark Paid — {row.user_name}
                    </DialogTitle>
                    <DialogDescription className="text-[#A0AAB2]">
                        {row.payout_method ? `via ${row.payout_method}. ` : ''}
                        Enter any amount ≤ remaining. Partial payments keep the row in the queue.
                    </DialogDescription>
                </DialogHeader>

                <div className="grid grid-cols-3 gap-2 pt-1 pb-3 border-b border-white/10 text-center">
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Owed (net)</div>
                        <div className="text-lg font-mono font-black text-white" data-testid="partial-original">{fmt(owedNet)}</div>
                        {owedNet !== Math.abs(row.amount || 0) && <div className="text-[9px] text-zinc-500 font-mono" data-testid="partial-gross">{fmt(Math.abs(row.amount || 0))} gross</div>}
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Already paid</div>
                        <div className="text-lg font-mono font-black text-emerald-300" data-testid="partial-paid-total">{fmt(paidTotal)}</div>
                    </div>
                    <div>
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500">Remaining</div>
                        <div className="text-lg font-mono font-black text-amber-300" data-testid="partial-remaining">{fmt(remaining)}</div>
                    </div>
                </div>

                {/* Send-this-amount banner. Payments are recorded in NET dollars (what the
                    player receives), so the amount entered is exactly what to send. */}
                {(() => {
                    const fee = Number(row.fee_amount || 0);
                    const netRaw = Number(row.net_amount || 0);
                    if (fee <= 0 || netRaw <= 0) return null;
                    if (overRemaining) {
                        return (
                            <div className="rounded-lg p-3 border-2 border-rose-500/70 bg-rose-500/10 space-y-1" data-testid="mark-paid-over-warning">
                                <div className="text-[10px] uppercase tracking-widest text-rose-300 font-black">Do not send this amount</div>
                                <p className="text-[12px] text-rose-100 leading-snug">
                                    {fmt(Number(amount))} is more than the {fmt(remaining)} still owed. The most you can record is {fmt(remaining)}.
                                </p>
                            </div>
                        );
                    }
                    const sendNow = Number(amount) || remaining;
                    const feeThisPay = fee * (sendNow / netRaw);
                    return (
                        <div
                            className="rounded-lg p-3 border-2 border-[#39FF14]/60 bg-[#39FF14]/8 space-y-1"
                            data-testid="mark-paid-send-banner"
                        >
                            <div className="flex items-center justify-between">
                                <span className="text-[10px] uppercase tracking-widest text-[#39FF14] font-black">
                                    Send to player via {row.payout_method || 'wallet app'}
                                </span>
                                <span className="text-[9px] text-[#A0AAB2] uppercase tracking-wider">
                                    after {Math.round(Number(row.fee_pct || 0) * 100) || 6}% fee
                                </span>
                            </div>
                            <div
                                className="text-3xl font-mono font-black text-[#39FF14] leading-none"
                                data-testid="mark-paid-send-net"
                            >
                                {fmt(sendNow)}
                            </div>
                            <p className="text-[11px] text-[#A0AAB2] leading-snug" data-testid="mark-paid-ledger-note">
                                Ledger records <span className="text-white font-bold">{fmt(sendNow)}</span>{' '}
                                paid of {fmt(owedNet)} net owed. Fee <span className="text-white">{fmt(feeThisPay)}</span> stays with the house.
                            </p>
                        </div>
                    );
                })()}

                {/* Payout QR embedded INSIDE the dialog — Jun 2026.
                    Previously the Mark-Paid button opened a separate full-screen
                    QR overlay (plain div, z-9999) at the same time as this Radix
                    modal. Radix sets pointer-events:none on <body>, so that
                    overlay (outside the portal) became unclickable and trapped
                    the admin — they couldn't close the QR or reach this dialog.
                    Showing the QR here keeps it to ONE interactive layer. */}
                {row.payout_qr_data_url && (
                    <div
                        className="flex flex-col items-center gap-2 rounded-lg p-3 border border-[#FFD700]/40 bg-[#FFD700]/5"
                        data-testid="mark-paid-qr-block"
                    >
                        <span className="text-[10px] uppercase tracking-widest text-[#FFD700] font-black">
                            Scan to pay via {row.payout_method || 'wallet app'}
                        </span>
                        <img
                            src={row.payout_qr_data_url}
                            alt="Player payment QR"
                            className="w-52 h-52 rounded-lg border-2 border-[#FFD700] object-contain bg-white"
                            data-testid="mark-paid-qr-image"
                        />
                        <a
                            href={row.payout_qr_data_url}
                            download={`payout-qr-${row.id}.png`}
                            className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#39FF14] hover:underline"
                            data-testid="mark-paid-qr-download"
                        >
                            <Download className="w-3.5 h-3.5" /> Save QR to device
                        </a>
                    </div>
                )}

                <div className="space-y-3 pt-1">
                    {row.is_ambassador ? (
                        <p className="text-[11px] text-[#A0AAB2] italic" data-testid="partial-ambassador-notice">
                            Ambassador PAY DAY is paid in one shot — no partials. Confirming will mark this cashout fully paid.
                        </p>
                    ) : (
                        <>
                        <div>
                            <label className="text-white text-xs">Pay amount ($)</label>
                            <div className="flex gap-1.5 mt-1">
                                <Input
                                    type="number" min="0" step="0.01" inputMode="decimal"
                                    value={amount}
                                    onChange={(e) => setAmount(e.target.value)}
                                    disabled={busy || remaining <= 0}
                                    className="bg-[#0a0a0a] border-white/10 text-white font-mono flex-1"
                                    data-testid="partial-amount-input"
                                />
                                <Button
                                    type="button" variant="outline"
                                    onClick={() => setAmount(String(remaining))}
                                    disabled={busy || remaining <= 0}
                                    className="border-zinc-700 bg-zinc-900 text-zinc-300 hover:bg-zinc-800 h-9 px-3 text-xs whitespace-nowrap"
                                    data-testid="partial-fill-remaining"
                                >
                                    Fill {fmt(remaining)}
                                </Button>
                            </div>
                        </div>
                        <div>
                            <label className="text-white text-xs">Note (optional)</label>
                            <Input
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                disabled={busy}
                                placeholder="e.g. sent via Cash App, $60 today"
                                className="mt-1 bg-[#0a0a0a] border-white/10 text-white text-sm"
                                data-testid="partial-note-input"
                            />
                        </div>
                        </>
                    )}
                </div>

                {!row.is_ambassador && installments.length > 0 && (
                    <div className="pt-3 mt-3 border-t border-white/10 space-y-1.5">
                        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-bold">
                            Payment history ({installments.length})
                        </div>
                        {installments.map((inst) => (
                            <div
                                key={inst.id}
                                className="flex items-center gap-2 text-[11px] py-1.5 px-2 rounded bg-zinc-900/50 border border-white/5"
                                data-testid={`partial-installment-${inst.id}`}
                            >
                                {editingId === inst.id ? (
                                    <>
                                        <Input
                                            type="number" min="0" step="0.01" inputMode="decimal"
                                            value={editAmount}
                                            onChange={(e) => setEditAmount(e.target.value)}
                                            disabled={busy}
                                            className="bg-[#0a0a0a] border-white/10 text-white font-mono h-7 w-24 text-xs"
                                            data-testid={`partial-edit-input-${inst.id}`}
                                        />
                                        <Button
                                            type="button" size="sm"
                                            onClick={() => saveEdit(inst)}
                                            disabled={busy}
                                            className="h-7 px-2 bg-emerald-500 hover:bg-emerald-400 text-zinc-900"
                                            data-testid={`partial-edit-save-${inst.id}`}
                                        >
                                            <Check className="w-3 h-3" />
                                        </Button>
                                        <Button
                                            type="button" size="sm" variant="outline"
                                            onClick={() => setEditingId(null)}
                                            disabled={busy}
                                            className="h-7 px-2 border-zinc-700 bg-zinc-900 text-zinc-300"
                                            data-testid={`partial-edit-cancel-${inst.id}`}
                                        >
                                            <X className="w-3 h-3" />
                                        </Button>
                                    </>
                                ) : (
                                    <>
                                        <span className="font-mono font-black text-emerald-300 w-20" data-testid={`partial-installment-amount-${inst.id}`}>{fmt(inst.amount)}</span>
                                        <span className="text-zinc-500 flex-1 truncate">
                                            {fmtDate(inst.paid_at)}
                                            {inst.note && <span className="italic ml-1">· {inst.note}</span>}
                                            {inst.edited_at && <span className="text-amber-300/80 ml-1">· edited</span>}
                                        </span>
                                        <button
                                            type="button"
                                            onClick={() => startEdit(inst)}
                                            disabled={busy}
                                            className="p-1 rounded hover:bg-white/5 text-zinc-400 hover:text-white disabled:opacity-40"
                                            title="Edit amount"
                                            data-testid={`partial-edit-btn-${inst.id}`}
                                        >
                                            <Pencil className="w-3 h-3" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => deleteInstallment(inst)}
                                            disabled={busy}
                                            className="p-1 rounded hover:bg-rose-500/10 text-rose-400 hover:text-rose-300 disabled:opacity-40"
                                            title="Delete installment"
                                            data-testid={`partial-delete-btn-${inst.id}`}
                                        >
                                            <Trash2 className="w-3 h-3" />
                                        </button>
                                    </>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                <DialogFooter className="pt-3">
                    <Button
                        variant="outline"
                        onClick={onClose}
                        disabled={busy}
                        className="border-zinc-700 bg-transparent text-zinc-300 hover:bg-zinc-800"
                        data-testid="partial-cancel-btn"
                    >
                        Close
                    </Button>
                    <Button
                        onClick={submitPayment}
                        disabled={busy || remaining <= 0 || overRemaining}
                        className="bg-emerald-500 hover:bg-emerald-400 text-zinc-900 font-black"
                        data-testid="partial-submit-btn"
                    >
                        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Check className="w-4 h-4 mr-1" />}
                        Record payment
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

export default PartialPaidDialog;
